import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SubscriptionManagement({ config, user }) {
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.error("Error fetching subscription:", err);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/subscription/create-checkout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Error creating checkout session");
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/subscription/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe customer portal
      window.location.href = response.data.portal_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Error opening billing portal");
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You will lose access at the end of your billing period.")) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert("Your subscription will be cancelled at the end of the billing period.");
      fetchSubscriptionStatus();
    } catch (err) {
      setError(err.response?.data?.detail || "Error cancelling subscription");
    } finally {
      setLoading(false);
    }
  };

  const isActive = user?.subscription_status === "active" || subscriptionStatus?.active;

  return (
    <div>
      <div className="card" data-testid="subscription-management">
        <h3>Subscription & Billing</h3>
        
        {error && <div className="alert alert-error">{error}</div>}

        {/* Subscription Status */}
        <div style={{ 
          padding: "15px", 
          background: isActive ? "#1a4d1a" : "#4d1a1a", 
          borderRadius: "6px",
          marginBottom: "20px",
          border: `1px solid ${isActive ? "#90EE90" : "#ff6b6b"}`
        }}>
          <p style={{ margin: "0 0 8px 0", fontSize: "1rem", color: isActive ? "#90EE90" : "#ff6b6b" }}>
            <strong>{isActive ? "✓ Active Subscription" : "✗ No Active Subscription"}</strong>
          </p>
          <p style={{ margin: "0", fontSize: "0.9rem", color: "#eee" }}>
            ${config?.SUBSCRIPTION_PRICE || 19}/month
          </p>
          {subscriptionStatus?.current_period_end && (
            <p style={{ margin: "8px 0 0 0", fontSize: "0.85rem", color: "#aaa" }}>
              {subscriptionStatus.cancel_at_period_end 
                ? `Cancels on: ${new Date(subscriptionStatus.current_period_end * 1000).toLocaleDateString()}`
                : `Renews on: ${new Date(subscriptionStatus.current_period_end * 1000).toLocaleDateString()}`
              }
            </p>
          )}
        </div>

        {/* What's Included */}
        <div className="card" style={{ background: "#222", marginBottom: "15px" }}>
          <h4 style={{ margin: "0 0 10px 0", fontSize: "1rem", color: "#FFD700" }}>
            What's Included:
          </h4>
          <ul style={{ margin: 0, paddingLeft: "20px" }}>
            <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
              Unlimited access to all courses
            </li>
            <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
              K-12 & Advanced curriculum
            </li>
            <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
              AI-generated quizzes & exams
            </li>
            <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
              Ministry training & ordination path
            </li>
            <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
              Exam system & transcript tracking
            </li>
            <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
              Certificate generation upon completion
            </li>
            <li style={{ background: "transparent", border: "none", padding: "0" }}>
              Offline access with PWA
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        {!isActive ? (
          <button 
            onClick={handleSubscribe}
            disabled={loading}
            data-testid="subscribe-button"
            style={{ 
              marginTop: "10px",
              background: "#FFD700",
              color: "#000",
              fontWeight: "bold"
            }}
          >
            {loading ? "Processing..." : `Subscribe Now - $${config?.SUBSCRIPTION_PRICE || 19}/month`}
          </button>
        ) : (
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button 
              onClick={handleManageSubscription}
              disabled={loading}
              data-testid="manage-subscription-button"
              style={{ flex: 1, marginTop: "10px" }}
            >
              {loading ? "Loading..." : "Manage Billing"}
            </button>
            <button 
              onClick={handleCancelSubscription}
              disabled={loading || subscriptionStatus?.cancel_at_period_end}
              data-testid="cancel-subscription-button"
              style={{ 
                flex: 1, 
                marginTop: "10px",
                background: "#4d1a1a",
                borderColor: "#ff6b6b"
              }}
            >
              {subscriptionStatus?.cancel_at_period_end ? "Cancellation Pending" : "Cancel Subscription"}
            </button>
          </div>
        )}

        {/* Payment Info */}
        <div className="card" style={{ background: "#1a3a4d", marginTop: "15px" }}>
          <p style={{ margin: "0 0 8px 0", fontSize: "0.9rem", color: "#87CEEB" }}>
            <strong>🔒 Secure Payment Processing</strong>
          </p>
          <p style={{ margin: "0", fontSize: "0.85rem", color: "#eee" }}>
            All payments are securely processed by Stripe. We never store your card details.
          </p>
        </div>

        {/* Support Info */}
        <div style={{ 
          marginTop: "20px", 
          padding: "15px", 
          background: "#1a1a1a", 
          borderRadius: "6px",
          fontSize: "0.85rem",
          color: "#aaa"
        }}>
          <p style={{ margin: "0 0 8px 0" }}>
            <strong>Need Help?</strong>
          </p>
          <p style={{ margin: "0" }}>
            Contact support: {config?.SUPPORT_EMAIL || "Yisraellearningcenter@gmail.com"}
          </p>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionManagement;
